import { useState } from 'react'
import useStore from '../../hooks/use_store'
import { statusTypes } from '../../constants/status_colour_map'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Table, Tag, ButtonDropdown } from '@bitcine/cinesend-theme'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import openInNewTab from '../../helpers/openinnewtab'
import RecallModal from './recall_modal'
import ImportForm from './import_form'
import Page from '../layouts/page'
import useUtilities from '../../hooks/use_utilities'
import CreateAssetButton from './create_asset_button'
import useTableFilters from '../../hooks/use_table_filters'
import { convertToShorthand } from '../../helpers/convert_date'
import CycleIsValidatingTag from '../cycles_table/validation_status_tag'
import { CycleStatus } from '../../constants/cycle_statuses'
import { MISSING_UNIQUE_NAME } from '../../constants/category_display'
import { AnimatedRefresh } from '../icons'

const AssetsTable = ({
  cycle,
  assetType: { value: assetTypeValue, label: assetLabel, plural_label: assetLabelPlural },
}) => {
  const cycleID = cycle.id
  const navigate = useNavigate()
  const { assetTypes } = useUtilities()
  const queryClient = useQueryClient()
  const { createModel, deleteModel } = useStore()
  const [recallModal, setRecallModal] = useState(false)
  const queryKey = `cycles/${cycleID}/assets?assetType=${assetTypeValue}`
  const recallableAssetsQueryKey = `cycles/${cycle.id}/recallable-assets?assetType=${assetTypeValue}`
  const { data: recallableAssets } = useQuery([recallableAssetsQueryKey])
  const [isModalOpen, setIsModalOpen] = useState(false)

  const sortingOptions = [
    { label: 'Status', key: 'status' },
    { label: 'Start Date', key: 'start_date' },
    { label: 'End Date', key: 'end_date' },
    { label: 'Title', key: 'title' },
  ]

  const filteringOptions = [
    {
      label: 'Type',
      key: 'type',
      type: 'checkboxes',
      options: [
        {
          label: 'New',
          key: 'new',
          value: 'new',
        },
        {
          label: 'Recalled',
          key: 'recalled',
          value: 'recalled',
        },
        {
          label: 'Expiring',
          key: 'expiring',
          value: 'expiring',
        },
        {
          label: 'Holdover',
          key: 'holdover',
          value: 'holdover',
        },
      ],
    },
  ]

  const { queryString, searching, sorting, filtering, pagination } = useTableFilters({
    filteringOptions,
    sortingOptions,
    enableSearch: true,
    queryExists: true,
    persistKey: `assets-table-${cycleID}-${assetTypeValue}`,
  })

  const { data, isLoading, isError, error } = useQuery({
    queryKey: [queryKey, queryString],
    queryFn: async ({ queryKey }) => {
      const response = await axios.get(`${import.meta.env.VITE_API_URL}/${queryKey[0]}&${queryKey[1]}`)
      return response.data
    },
    refetchInterval: (data) => {
      const shouldPoll = data?.import?.status === 'pending' || data?.import?.status === 'confirmation_in_progress'
      return shouldPoll ? 2000 : false
    },
    refetchIntervalInBackground: false, // only refetch when window is in focus
  })

  const isOpenCycle = cycle.status === CycleStatus.Open

  const openModal = () => setIsModalOpen(true)
  const closeModal = () => {
    queryClient.invalidateQueries(queryKey)
    setIsModalOpen(false)
  }
  const goToAsset = (data) => navigate(`${data.id}`)

  const exportAssets = ({ isTemplate }) => {
    createModel.mutate(
      {
        endpoint: 'exports',
        data: {
          name: `export-cycle-${cycleID}-assetType-${assetTypeValue}`,
          cycleID: cycleID,
          type: assetTypeValue,
          isTemplate,
        },
      },
      {
        onSuccess: (res) => {
          if (res.data) {
            axios.get(`${process.env.VITE_API_URL}/exports/${res.data.export}/download`).then((r) => {
              openInNewTab(r.data.url)
            })
          }
        },
      }
    )
  }

  const showValidatorsResult = (data) => {
    return (
      <div className='flex flex-wrap gap-1'>
        {data.errors > 0 && <Tag label={`Errors: ${data.errors}`} type='error' outline={true} />}
        {data.warnings > 0 && <Tag label={`Warnings: ${data.warnings}`} type='warning' outline={true} />}
        {!data.warnings && !data.errors && <Tag label='OK' type='success' outline={true} />}
      </div>
    )
  }

  return (
    <Page
      title={
        <>
          {assetLabelPlural} <CycleIsValidatingTag is_validating={cycle?.is_validating} />
        </>
      }
      buttons={
        assetTypeValue !== 'featured-content' && [
          <div key={'asset-buttons'} className='flex items-center space-x-2'>
            <CreateAssetButton cycle={cycle} assetType={assetTypeValue} assetLabel={assetLabel} />
            {assetTypeValue !== 'ad' && (
              <>
                <ButtonDropdown
                  button={{
                    text: 'Export',
                    icon: 'cloud_download',
                  }}
                  dropdown={{
                    content: [
                      {
                        text: 'Export Template',
                        onClick: () => exportAssets({ isTemplate: true }),
                      },
                      {
                        text: 'Export Populated',
                        onClick: () => exportAssets({ isTemplate: false }),
                      },
                    ],
                  }}
                />
                <Button
                  icon={data?.import?.status === 'pending' ? AnimatedRefresh : 'cloud_upload'}
                  onClick={openModal}
                  disabled={!isOpenCycle}
                  title={!isOpenCycle ? 'Imports are only allowed on open cycles' : undefined}
                >
                  {`Import ${assetTypeValue === 'series' ? 'TV Episodes' : assetLabelPlural}`}
                </Button>
              </>
            )}

            {recallableAssets?.length > 0 && (
              <Button icon='refresh' onClick={() => setRecallModal(true)}>
                Recall {assetLabelPlural}
              </Button>
            )}
          </div>,
        ]
      }
    >
      <Table
        status={{
          pending: isLoading || createModel.isLoading,
          error: isError,
          errorMessage: error?.message,
        }}
        widths={
          assetTypeValue === 'ad'
            ? [200, 150, 100, 220, 55]
            : ['music', 'podcast', 'audiobook'].includes(assetTypeValue)
              ? [200, 150, 100, 100, 100, 55]
              : [200, 150, 100, 220, 100, 100, 55]
        }
        header={{
          columns: [
            { text: 'Asset Title', key: 'title' },
            { text: 'Validators', key: '' },
            { text: 'Status', key: 'status' },
            ...(!['music', 'podcast', 'audiobook'].includes(assetTypeValue) ? [{ text: 'Pre-Roll' }] : []),
            ...(assetTypeValue !== 'ad'
              ? [
                  { text: 'Start Date', key: 'start_date' },
                  { text: 'End Date', key: 'end_date' },
                ]
              : []),
            { text: '' },
          ],
          searching,
          sorting,
          filtering,
        }}
        body={{
          data: data?.data,
          row: {
            compact: true,
            spaced: true,
            onClick: (event, data) => goToAsset(data),
            render: [
              (data) => data.default_title,
              (data) => showValidatorsResult(data),
              (data) => <Tag outline type={statusTypes[data?.status.toLowerCase()]} label={data.status ?? 'N/A'} />,
              ...(!['music', 'podcast', 'audiobook'].includes(assetTypeValue)
                ? [
                    (data, index) => (
                      <div className='py-2 flex flex-wrap gap-2'>
                        {data.categoryLabels
                          ?.filter((category) => category.type === 'warning-slates' || category.type === 'ad-pools')
                          .map((category) => (
                            <Tag
                              key={`${category.id}-${index}`}
                              outline
                              label={
                                category.type === 'warning-slates'
                                  ? category.value
                                  : category.external_unique_name || MISSING_UNIQUE_NAME
                              }
                            />
                          ))}
                      </div>
                    ),
                  ]
                : []),
              ...(assetTypeValue !== 'ad'
                ? [(data) => convertToShorthand(data.start_date), (data) => convertToShorthand(data.end_date)]
                : []),
              (data) =>
                data?.status === 'Holdover' || data?.status === 'Expiring' ? null : (
                  <Button
                    icon='delete'
                    minimal
                    type='error'
                    onClick={() =>
                      deleteModel.mutate({
                        endpoint: `cycles/${cycleID}/assets`,
                        id: data.id,
                        onSuccess: () => queryClient.invalidateQueries(queryKey),
                        confirmMessage: `Are you sure you want to delete ${data.title}?${
                          assetTypes.some((type) => type.value === data.asset_type && type.is_parent)
                            ? ' Deleting this asset will also delete all child assets.'
                            : ''
                        }`,
                      })
                    }
                  />
                ),
            ],
          },
          empty: {
            title: `No ${assetLabelPlural.toLowerCase()} found!`,
            text: `Create a new ${assetLabel.toLowerCase()} to begin.`,
            icon: 'article',
          },
        }}
        paginate={{
          ...pagination,
          totalRows: data?.meta.total ?? 0,
          options: [10, 25, 50, 100, 250, 500, 1000],
        }}
      />
      {isModalOpen && (
        <ImportForm
          isOpen={isModalOpen}
          onClose={closeModal}
          cycleID={cycleID}
          assetType={assetTypeValue}
          importData={data?.import}
          queryKey={[queryKey, queryString]}
        />
      )}
      {recallModal && (
        <RecallModal
          currentAssetsQueryKey={queryKey}
          recallableAssetsQueryKey={recallableAssetsQueryKey}
          id={cycleID}
          label={assetLabelPlural}
          onClose={() => {
            setRecallModal(false)
            queryClient.invalidateQueries(queryKey)
          }}
        />
      )}
    </Page>
  )
}

export default AssetsTable
